import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { FileDown, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { client } from '../api/client';
import { formatPhone } from '../lib/phone';
import { submissionStatusLabel } from '../lib/submissionState';
import {
  downloadCandidateProfilePdf,
  downloadCandidateProfilePdfByCandidate,
  exportErrorMessage,
} from '../lib/exportDownload';
import {
  exclusionSummary,
  formatDayMonthYear,
  interviewModeLabel,
  interviewStatusLabel,
  noticeLabel,
} from '../lib/candidateOptions';
import { useCustomFields } from './CustomFieldInputs';
import type { Candidate, CandidateCertificate } from '../types';
import { titleCaseName } from '../lib/titleCase';
import { experienceYearsMonths } from '../lib/experience';
import { useDismissOnEscape } from '../lib/useDismiss';

/**
 * Everything recorded about one candidate, read-only.
 *
 * A separate panel from the edit dialog rather than a read mode of it. The
 * dialog exists to correct four things and is shaped around that; this exists
 * to answer "who is this person" without the risk of changing them by
 * accident, and the two want opposite layouts.
 *
 * Fetched fresh by id rather than shown from the row already in hand. The list
 * is paginated and filtered, so the row may be minutes old, and a detail view
 * that quietly disagrees with the record is worse than one that takes a moment
 * to load.
 *
 * Empty values read as a dash rather than being hidden. Which fields are blank
 * is itself the answer to "what do we still need from them", and a panel that
 * silently drops them makes a half-filled record look complete.
 */
function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-3 py-1.5">
      <dt className="w-44 shrink-0 text-xs text-gray-500">{label}</dt>
      <dd className="min-w-0 break-words text-gray-900">
        {value === null || value === undefined || value === '' ? (
          <span className="text-gray-300">—</span>
        ) : (
          value
        )}
      </dd>
    </div>
  );
}

function experience(candidate: Candidate): string | null {
  const { experienceMonths, experienceRaw } = candidate;
  if (experienceMonths === null) return experienceRaw;
  // The shared formatter, which the candidates table now uses too. This
  // panel had its own copy of the same arithmetic and the same `Fresher`;
  // two of them agreeing today is not the same as two of them agreeing.
  const label = experienceYearsMonths(experienceMonths);
  // The original wording is kept because parsing it into months was lossy.
  return experienceRaw ? `${label} (${experienceRaw})` : label;
}

export default function CandidateDetailPanel({
  candidateId,
  name,
  onClose,
}: {
  candidateId: string;
  /** Shown while it loads, so the panel is never anonymous. */
  name?: string | null;
  onClose: () => void;
}) {
  // Escape closes the topmost drawer. Added alongside the month
  // panel's: none of these listened for it, which was consistent and
  // consistently wrong, and giving it to one would have made that one
  // the odd drawer out.
  useDismissOnEscape(onClose);

  const detail = useQuery<Candidate>({
    queryKey: ['candidate', candidateId],
    queryFn: async () =>
      (await client.get(`/api/v1/candidates/${candidateId}`)).data,
    retry: false,
  });

  /** Their certificates, the same rows the edit dialog maintains. */
  const certificates = useQuery<CandidateCertificate[]>({
    queryKey: ['candidates', candidateId, 'certificates'],
    queryFn: async () =>
      (await client.get(`/api/v1/candidates/${candidateId}/certificates`)).data,
    retry: false,
  });

  // The definitions, so a custom value can be shown under its own label
  // rather than as a field id nobody recognises.
  const customFields = useCustomFields('candidate');
  const certificateNames =
    (certificates.data ?? []).map((row) => row.name).join(', ') || null;
  const definitions = customFields.data?.items ?? [];

  const c = detail.data;
  const values = c?.customValues ?? {};

  const [downloadingPdf, setDownloadingPdf] = useState(false);

  /**
   * The profile as a one-page document.
   *
   * Two routes behind one button, chosen by whether this person has been put
   * forward to anybody. With a submission the document names the client, the
   * role and the date it was sent — which is the context a copy going to that
   * client needs, and why the server keys that copy on the submission rather
   * than on the candidate. Without one it prints the person alone; the
   * candidate route refuses to guess at the latest submission's client, so
   * asking it is the only way to get a document for somebody still being
   * worked on.
   *
   * Not a useMutation: nothing server-side moves and there is nothing to
   * invalidate. The endpoint needs the Authorization header, which is why it
   * cannot be a plain link — see lib/exportDownload.
   */
  async function handleDownloadPdf() {
    if (!c) return;
    setDownloadingPdf(true);
    try {
      const name = c.latestSubmissionId
        ? await downloadCandidateProfilePdf(c.latestSubmissionId, c.fullName)
        : await downloadCandidateProfilePdfByCandidate(c.id, c.fullName);
      toast.success(`Saved ${name}`);
    } catch (e) {
      // The blob-aware reader: with responseType 'blob' even a 404 arrives as
      // binary, so the ordinary helper would lose the server's message.
      toast.error(await exportErrorMessage(e, 'Could not build that PDF.'));
    } finally {
      setDownloadingPdf(false);
    }
  }
  //: Which statutory deductions they had at a previous employer. Null when
  //: none are ticked, so the row reads as "—" rather than as an empty claim.
  const deductions =
    [
      c?.hadPt ? 'PT' : null,
      c?.hadEsi ? 'ESI' : null,
      c?.hadTds ? 'TDS' : null,
    ]
      .filter(Boolean)
      .join(', ') || null;

  return (
    <div className="fixed inset-0 z-30 flex justify-end bg-black/30" onClick={onClose}>
      <aside
        className="flex h-full w-full max-w-lg flex-col bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
          <div className="min-w-0">
            <h3 className="truncate text-sm font-semibold text-gray-900">
              {titleCaseName(c?.fullName) ?? name ?? 'Candidate'}
            </h3>
            <p className="mt-0.5 text-xs text-gray-500">
              Everything on file. Use Edit to change any of it.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Offered for anybody the panel can show, which before this
                excluded every candidate nobody had submitted yet — most of
                the pool. Waits for the record rather than acting on the row
                the caller passed, because which document gets built depends
                on a field only the fetched record carries. The title says
                which one it will be, so a recruiter is not surprised by a
                copy with no client on it. */}
            {c && (
              <button
                type="button"
                className="btn-ghost text-xs"
                disabled={downloadingPdf}
                onClick={() => void handleDownloadPdf()}
                title={
                  c.latestSubmissionId
                    ? `Download profile as PDF, prepared for ${
                      c.latestSubmissionClientName ?? 'the client they were sent to'
                    }`
                    : 'Download profile as PDF. Not submitted to anybody yet, so no client is named on it'
                }
              >
                {downloadingPdf ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <FileDown className="h-4 w-4" />
                )}
                PDF
              </button>
            )}
            <button type="button" className="btn-ghost" onClick={onClose} aria-label="Close">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {detail.isLoading ? (
          <p className="px-5 py-8 text-sm text-gray-400">Loading…</p>
        ) : detail.isError || !c ? (
          <p className="px-5 py-8 text-sm text-gray-500">
            That candidate could not be loaded.
          </p>
        ) : (
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-4 text-sm">
            <section>
              <h4 className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                Contact Details
              </h4>
              <dl className="divide-y divide-gray-100">
                <Row label="Mobile" value={formatPhone(c.phone)} />
                <Row label="Alternate number" value={formatPhone(c.altPhone)} />
                <Row label="Email" value={c.email} />
                {/* DD/MM/YYYY, like every other date on these screens. The
                    API sends ISO, which nobody in this office reads as
                    anything but an American date with the year moved. */}
                <Row label="Date of birth" value={formatDayMonthYear(c.dateOfBirth)} />
              </dl>
            </section>

            <section>
              <h4 className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                Education Details
              </h4>
              <dl className="divide-y divide-gray-100">
                <Row label="Qualification" value={c.qualification} />
                <Row label="Experience" value={experience(c)} />
                <Row label="Designation" value={c.designation} />
                {/* One column, labelled by the flag. Null reads plainly as
                    "Company" rather than claiming either way. */}
                <Row
                  label={
                    c.currentlyEmployed === true
                      ? 'Present company'
                      : c.currentlyEmployed === false
                        ? 'Previous company'
                        : 'Company'
                  }
                  value={c.currentCompany}
                />
                <Row label="Certifications" value={certificateNames} />
                <Row label="Staying location" value={c.currentLocation} />
                <Row label="Preferred location" value={c.preferredLocation} />
              </dl>
            </section>

            <section>
              <h4 className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                Salary Details
              </h4>
              <dl className="divide-y divide-gray-100">
                <Row label="Present salary" value={c.currentSalary} />
                {/* Directly under the figure, because it is what the figure
                    means: "32,000 excluding PF, incentives" and a flat
                    "32,000" are thousands apart, and the second one is what
                    an offer gets built on when nobody wrote this down. */}
                <Row label="Excluding" value={exclusionSummary(c)} />
                {/* Not a property of the figure above it — a record of what
                    the candidate has been on before. */}
                <Row label="Had at previous employer" value={deductions} />
                <Row label="Expected salary" value={c.expectedSalary} />
                <Row label="Notice period" value={noticeLabel(c.noticePeriodDays)} />
              </dl>
            </section>

            {/* What the recruiter recorded by hand, and what the pipeline
                derived, side by side rather than merged. The table collapses
                them into one column because a row has no space for both; a
                panel answering "who is this person" has room to say that the
                recruiter called it a final round while the submission is
                still sitting at shortlisted, which is a disagreement worth
                seeing rather than resolving silently. */}
            <section>
              <h4 className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                Interview
              </h4>
              <dl className="divide-y divide-gray-100">
                <Row
                  label="Interview status"
                  value={interviewStatusLabel(c.interviewStatus)}
                />
                <Row label="Mode" value={interviewModeLabel(c.interviewMode)} />
                <Row
                  label="Date of joining"
                  value={formatDayMonthYear(c.dateOfJoining)}
                />
                {/* The scheduled round, where there is one. Its own rows
                    because they come from an Interview record against a
                    submission rather than from anything typed here. */}
                <Row
                  label="Scheduled round"
                  value={formatDayMonthYear(c.latestInterviewOn)}
                />
                <Row
                  label="Scheduled mode"
                  value={interviewModeLabel(c.latestInterviewMode)}
                />
                <Row
                  label="Submission status"
                  value={
                    c.latestSubmissionStatus
                      ? submissionStatusLabel(c.latestSubmissionStatus)
                      : null
                  }
                />
              </dl>
            </section>

            <section>
              <h4 className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                Resume
              </h4>
              <dl className="divide-y divide-gray-100">
                {/* Both, because they answer different questions and can
                    disagree — a promise kept without a file attached, or a
                    file attached that was never promised. */}
                <Row
                  label="File on record"
                  value={
                    c.hasResume ? (
                      <span className="text-emerald-700">Yes</span>
                    ) : (
                      <span className="text-gray-500">No file uploaded</span>
                    )
                  }
                />
                <Row label="Promised by" value={c.resumeDueAt} />
                <Row label="Marked received" value={c.resumeReceivedAt} />
              </dl>
            </section>

            {definitions.length > 0 && (
              <section>
                <h4 className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                  Additional Fields
                </h4>
                <dl className="divide-y divide-gray-100">
                  {definitions.map((field) => (
                    <Row
                      key={field.id}
                      label={field.label}
                      value={values[field.id] ?? null}
                    />
                  ))}
                </dl>
              </section>
            )}

            <section>
              <h4 className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                Other
              </h4>
              <dl className="divide-y divide-gray-100">
                {/* The date is on every call; the time is null on every
                    imported one, where the workbook recorded a day and no
                    clock. Shown together so a blank time reads as "not
                    recorded" beside a date that is. */}
                <Row
                  label="Latest call"
                  value={
                    c.latestCallDate
                      ? [
                        formatDayMonthYear(c.latestCallDate),
                        c.latestCallAt
                          ? new Date(c.latestCallAt).toLocaleTimeString('en-IN', {
                            hour: 'numeric',
                            minute: '2-digit',
                          })
                          : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')
                      : null
                  }
                />
                <Row label="Latest call client" value={c.latestCallClientName} />
              </dl>
            </section>
          </div>
        )}
      </aside>
    </div>
  );
}
